import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { getFleetMediaUrl } from "../../../../../api/fleet-management/api";

const GeneralExpenseForm = ({
  form,
  validationErrors,
  submitting,
  isEditMode,
  expenseTypeOptions,
  vehicles,
  drivers,
  isVehiclesLoading,
  isDriversLoading,
  onSubmit,
  onFieldChange,
  onUploadReceipt,
  onRemoveReceipt,
  isUploadingReceipt,
  isDeletingReceipt,
  onBack,
}) => {
  const isVehicleType = form.expenseType === "vehicle";
  const isDriverType = form.expenseType === "driver";

  return (
    <Form onSubmit={onSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Expense Type</Form.Label>
            <Form.Select
              value={form.expenseType}
              onChange={(e) => onFieldChange("expenseType", e.target.value)}
              isInvalid={!!validationErrors.expenseType}
            >
              {(expenseTypeOptions || []).map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.expenseType}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={form.amount}
              onChange={(e) => onFieldChange("amount", e.target.value)}
              placeholder="1000"
              isInvalid={!!validationErrors.amount}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.amount}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Optional expense description"
            />
          </Form.Group>
        </Col>

        {isVehicleType && (
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Vehicle</Form.Label>
              <Form.Select
                value={form.vehicleId}
                onChange={(e) => onFieldChange("vehicleId", e.target.value)}
                isInvalid={!!validationErrors.vehicleId}
                disabled={isVehiclesLoading}
              >
                <option value="">Select vehicle</option>
                {(vehicles || []).map((vehicle) => (
                  <option key={vehicle?._id} value={vehicle?._id}>
                    {vehicle?.name} - Seats: {vehicle?.specifications?.seatingCapacity || "N/A"}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.vehicleId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        )}

        {isDriverType && (
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Driver</Form.Label>
              <Form.Select
                value={form.driverId}
                onChange={(e) => onFieldChange("driverId", e.target.value)}
                isInvalid={!!validationErrors.driverId}
                disabled={isDriversLoading}
              >
                <option value="">Select driver</option>
                {(drivers || []).map((driver) => (
                  <option key={driver?._id} value={driver?._id}>
                    {driver?.name || "Unnamed Driver"}
                    {driver?.contactNumber ? ` (${driver.contactNumber})` : ""}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.driverId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        )}

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Receipt</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              disabled={isUploadingReceipt || isDeletingReceipt || submitting}
              onChange={(e) => onUploadReceipt(e.target.files?.[0])}
            />
            <Form.Text className="text-muted">
              Upload an image receipt. Relative media path is stored on the server.
            </Form.Text>
          </Form.Group>

          {form.receipt && (
            <div className="border rounded p-3 mb-3 d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="text-muted small mb-2">Current receipt</div>
                <img
                  src={getFleetMediaUrl(form.receipt)}
                  alt="Expense receipt"
                  style={{
                    width: "160px",
                    height: "110px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                  }}
                />
                <small className="text-muted d-block mt-2">{form.receipt}</small>
              </div>

              <Button
                type="button"
                variant="outline-danger"
                disabled={isDeletingReceipt || isUploadingReceipt || submitting}
                onClick={onRemoveReceipt}
              >
                {isDeletingReceipt ? "Removing..." : "Remove Receipt"}
              </Button>
            </div>
          )}
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Expense Date</Form.Label>
            <Form.Control
              type="date"
              value={form.expenseDate}
              onChange={(e) => onFieldChange("expenseDate", e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 mt-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              {isEditMode ? "Saving..." : "Creating..."}
            </>
          ) : (
            isEditMode ? "Save Changes" : "Create Expense"
          )}
        </Button>
        <Button type="button" variant="light" disabled={submitting} onClick={onBack}>
          Back
        </Button>
      </div>
    </Form>
  );
};

export default GeneralExpenseForm;
